import { observer } from "mobx-react-lite";
import React, { useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useDebouncedCallback } from "use-debounce";

import EllipsisLoader from "../components/EllipsisLoader";
import FloorplanCustomiser from "../components/FloorplanCustomiser";
import FloorplanLightbox from "../components/FloorplanLightbox";
import FullscreenModal from "../components/FullscreenModal";
import HouseFilters from "../components/HouseFilters";
import HouseTypeSelector from "../components/HouseTypeSelector";
import Icon from "../components/Icon";
import SearchResultsBar from "../components/SearchResultsBar";
import Selectable from "../components/Selectable";
import SeriesListing from "../components/SeriesListing";
import { ApiContext, NavigationContext, UserContext } from "../contexts";
import useWindowSize from "../hooks/useWindowSize";
import { getPageTitle } from "../resources/routes";
import { FloorplanOption,Home,HouseType,IconName,LandType,Series } from "../types";
import { getPriceFormatted,scrollToTop,scrollToWithOffset } from "../util";

const ChooseFloorplanScreen = observer(() => {
  const apiContext = useContext(ApiContext);
  const userContext = useContext(UserContext);
  const navContext = useContext(NavigationContext);
  const location = useLocation();

  const windowSize = useWindowSize();

  const [lightboxSvg, setLightboxSvg] = useState("");
  const [isLightboxShown, setIsLightboxShown] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalClosing, setIsModalClosing] = useState(false);
  const [hasSortChanged, setHasSortChanged] = useState(false);
  const [hasChosenSeries, setHasChosenSeries] = useState(false);

  const debouncedGetSeriesFromApi = useDebouncedCallback(() => {
    apiContext.resetHomeSearchPagination();
    apiContext.getSeries(undefined, hasSortChanged);
  }, 1000);

  const debouncedGetSeriesFromApiWithPage = useDebouncedCallback((page: number) => {
    apiContext.getSeries(page, hasSortChanged);
  }, 1000);

  useEffect(() => {
    scrollToTop();
    navContext.setFooterNavVisible(false);
    navContext.resetNextBtn();

    userContext.setSelectedFacade(null);
    userContext.resetSelectedFacadeFinishOption();

    navContext.setOnNavigateBack(() => {
      // Reset the form values when the user goes back
      apiContext.setSelectedSeries(null);
      apiContext.setHouseType(null);
      apiContext.resetHouseFilters();
      userContext.setSelectedHome(null);
      userContext.resetSelectedFloorplanOptions();
      userContext.resetIsFloorplanFlipped();
      navContext.setSubtotalVisible(false);
    });

    return () => {
      // Unbind the handler when we unmount
      navContext.resetOnNavigateBack();
    };

  }, []);

  useEffect(() => {
    // When search filters change, debounce search for new homes
    if (apiContext.houseType !== null) {
      apiContext.setIsLoading(true);
      userContext.setSelectedHome(null);
      apiContext.setSelectedSeries(null);
      debouncedGetSeriesFromApi();
      setHasChosenSeries(false);
      scrollToResults();
    }
  }, [
    apiContext.houseType,
    apiContext.numBeds,
    apiContext.numBaths,
    apiContext.numCars,
    apiContext.numLiving,
    apiContext.searchText,
    apiContext.lotWidth,
    apiContext.series,
    apiContext.sortMethod,
  ]);

  useEffect(() => {
    if (apiContext.enteredViaHome && !userContext.selectedHome) {
      scrollToSearch();
      const existingHomeIndex = apiContext.homeSearchResultsWithPlaceholders
        .map((item) => (item ? item.id : -1))
        .indexOf(apiContext.enteredViaHome.id);
      if (existingHomeIndex > -1) {
        userContext.setSelectedHome(
          apiContext.homeSearchResultsWithPlaceholders[existingHomeIndex]
        );

        // Now remove the entered via home link so they can choose other homes etc
        apiContext.setEnteredViaHome(null);
      }
    }
  }, [
    apiContext.homeSearchResultsWithPlaceholders,
    apiContext.enteredViaHome,
    userContext.selectedHome,
  ]);
  
  const onLoadMore = () => {
    if(apiContext.homeSearchResultsWithPlaceholders.length){
      if(apiContext.homeSearchCurrentPage < apiContext.homeSearchLastPage){
        debouncedGetSeriesFromApiWithPage(apiContext.homeSearchCurrentPage + 1);
      }
    }
  };

  const onClickSeries = (series: Series, index: number) => {
    if(hasChosenSeries && apiContext.selectedSeries && apiContext.selectedSeries.id == series.id){
      setHasChosenSeries(false);
    }else{
      if(series.homes) {
        const $selectedTab = $(".pyht-series-listing[data-id=\"" + series.id + "\"] .tab.active");
        if($selectedTab.length){
          series.homes.map((home, index) => {
            if($selectedTab.data("id") == home.id){
              userContext.setSelectedHome(home);
            }
          });
        }
      }
      apiContext.setSelectedSeries(series);
      setHasChosenSeries(true);
      if(windowSize.width >= 992){
        setTimeout(function(){
          scrollToWithOffset(".floorplan-wrapper", 0);
        }, 1);
      }else{
        scrollToTop();
      }
    }
  };

  const onClickHouseType = (option: HouseType) => {
    apiContext.toggleHouseType(option);
  };

  const scrollToSearch = () => {
    if (windowSize.width < 992) {
      setTimeout(function () {
        // https://zimpledigital.teamwork.com/#/tasks/223303 - Scroll to results section when selecting a House Type
        scrollToWithOffset(".house-type-section-label", -50);
      }, 250);
    }
  };

  const scrollToResults = () => {
    if (windowSize.width < 992) {
      setTimeout(function () {
        // https://zimpledigital.teamwork.com/#/tasks/223303 - Scroll to results section when selecting a House Type
        scrollToWithOffset(".pyht-search-results-bar", -50);
      }, 250);
    }
  };

  const onClickFloorplanOption = (homeId: number, option: FloorplanOption) => {
    userContext.toggleSelectedFloorplanOption(homeId, option);
  };

  const onFlipFloorplan = (homeId: number, isFlipped: boolean) => {
    userContext.setIsFloorplanFlipped(homeId, isFlipped);
  };

  const onClickShowLightbox = (svg: string) => {
    // The user can click the lightbox link on the next item in the carousel on desktop
    setLightboxSvg(svg);
    setIsLightboxShown(true);
  };

  const onCloseSearchModal = () => {
    setIsModalOpen(false);
    setIsModalClosing(false);
  };

  const onChangeSort = () => {
    setHasSortChanged(true);
  };

  const showPreviousHome = () => {
    const $selectedTab = $(".floorplan-wrapper .tab.active");
    if($selectedTab.prev() !== undefined){
      $selectedTab.prev().trigger("click");
    }else{
      $(".floorplan-wrapper .tab:last-child").trigger("click");
    }
  };

  const showNextHome = () => {
    const $selectedTab = $(".floorplan-wrapper .tab.active");
    if($selectedTab.next() !== undefined){
      $selectedTab.next().trigger("click");
    }else{
      $(".floorplan-wrapper .tab:first-child").trigger("click");
    }
  };

  const onSelectFloorplan = (id: number) => {
    apiContext.getSingleHome(id, userContext, navContext);
  };

  return (
    <div className={`screen screen-choose-floorplan ${hasChosenSeries ? " has-chosen-series" : ""}`}>
      {(windowSize.width >= 992 || !hasChosenSeries) && (
        <div className={"container"}>
          <h1 className="process-title">{ apiContext.processSettings.title }</h1>
          <div className="copy" dangerouslySetInnerHTML={{ __html: apiContext.processSettings.copy }} />
          {apiContext.processSteps.length > 0 && (
            <div className="steps">
              {apiContext.processSteps.map(
                (step, index) => {
                  return (
                    <React.Fragment key={index}>
                      <div className="divider"></div>
                      <div className="step">
                        <div className="step-circle">{ index + 1 }</div>
                        <div className="step-text">
                          <div className="step-title">{ step.title }</div>
                          <div className="copy">{ step.copy }</div>
                        </div>
                      </div>
                    </React.Fragment>
                  );
                }
              )}
            </div>
          )}
          {apiContext.processSettings.image && (
            <div className="process-image">
              <img src={ windowSize.width >= 992 || !apiContext.processSettings.image_mobile ? apiContext.processSettings.image : apiContext.processSettings.image_mobile } alt=""></img>
            </div>
          )}
          {apiContext.processSettings.video_file && (
            <div className="video">
              <div className="video-inner">
                <video controls playsInline>
                  <source src={ apiContext.processSettings.video_file } type="video/mp4"></source>
                </video>
              </div>
            </div>
          )}
          {apiContext.processSettings.video_url && (
            <div className="video">
              <div className="video-inner">
                <iframe src={ apiContext.processSettings.video_url }></iframe>
              </div>
            </div>
          )}
          <h2 className="h1 step-title"><strong className="number">Step 1:</strong> {getPageTitle(location.pathname)}</h2>
          <HouseTypeSelector
            onClick={onClickHouseType}
            selected={apiContext.houseType}
          />
        </div>
      )}
      {apiContext.houseType !== null ? (
        <div className={"container floorplans-container"}>
          {windowSize.width >= 992 ? (
            <div className={"filters-desktop"}>
              <p className={"section-label"}>Filter By</p>
              <HouseFilters
                beds={apiContext.numBeds}
                living={apiContext.numLiving}
                baths={apiContext.numBaths}
                cars={apiContext.numCars}
                searchText={apiContext.searchText}
                onClickBeds={(val) => apiContext.toggleNumBeds(val)}
                onClickLiving={(val) => apiContext.toggleNumLiving(val)}
                onClickBaths={(val) => apiContext.toggleNumBaths(val)}
                onClickCars={(val) => apiContext.toggleNumCars(val)}
                onSearchChange={(val) => apiContext.setSearchText(val)}
                onScreenKeyboard={userContext.isKioskMode}
                inlineLabels
              />
            </div>
          ) : (
            (windowSize.width >= 992 || !hasChosenSeries) && (
              <div
                className={"btn btn-secondary filters-mobile"}
                onClick={() => setIsModalOpen(true)}
              >
                Refine Your Search
                <Icon name={IconName.CHEVRON_DOWN} />
              </div>
            )
          )}
          {(windowSize.width >= 992 || !hasChosenSeries) && (
            <SearchResultsBar 
              landType={LandType.VACANT_BLOCK}
              onChangeSort={() => onChangeSort()}
            />
          )}
          {apiContext.isLoading && !apiContext.isLoadingNextPage ? (
            <></>
          ) : apiContext.homeSearchResultsWithPlaceholders.length > 0 ? (
            <div className={`floorplan-results ${apiContext.isLoadingNextPage ? "is-loading-next-page" : ""}`}>
              {apiContext.seriesSearchResultsWithPlaceholders.map(
                (series, index) => {
                  if (series == null) {
                    return (
                      <div key={index}>
                        <Selectable
                          selected={false}
                        >
                          <div className={"floorplan-loading"}>
                            <EllipsisLoader />
                          </div>
                        </Selectable>
                      </div>
                    );
                  }

                  return (
                    <>
                      <div>
                        {(windowSize.width >= 992 || !hasChosenSeries) && (
                          <Selectable
                            selected={
                              hasChosenSeries &&
                              apiContext.selectedSeries !== null &&
                            apiContext.selectedSeries.id === series.id
                            }
                          >
                            <SeriesListing
                              series={series}
                              isActive={
                                apiContext.selectedSeries !== null &&
                              apiContext.selectedSeries.id === series.id
                              }
                              selectedHome={userContext.selectedHome}
                              onClickSeries={() => onClickSeries(series, index)}
                            />
                          </Selectable>
                        )}
                        {apiContext.selectedSeries && hasChosenSeries && apiContext.selectedSeries.id === series.id && (
                          userContext.selectedHome && (
                            <div className="floorplan-wrapper">
                              {windowSize.width < 992 && (
                                <>
                                  <button className="back" onClick={() => setHasChosenSeries(false)}>
                                    <Icon name={IconName.ARROW_THIN_BLACK} />
                                    All Series
                                  </button>
                                  <div className="mobile-details">
                                    <h2 className="home-title">{ userContext.selectedHome.title }</h2>
                                    <div className="price">
                                      <span className="from">From </span>
                                      {getPriceFormatted(userContext.selectedHome.price)}
                                    </div>
                                    <div className="rooms">
                                      <div className={"room"}>
                                        <Icon name={IconName.BED} />
                                        <span>{userContext.selectedHome.bedrooms}</span>
                                      </div>
                                      <div className={"room"}>
                                        <Icon name={IconName.COUCH} />
                                        <span>{userContext.selectedHome.living_areas}</span>
                                      </div>
                                      <div className={"room"}>
                                        <Icon name={IconName.BATH} />
                                        <span>{userContext.selectedHome.bathrooms}</span>
                                      </div>
                                      <div className={"room"}>
                                        <Icon name={IconName.CAR} />
                                        <span>{userContext.selectedHome.car_spaces}</span>
                                      </div>
                                      <div className={"room"}>
                                        <Icon name={IconName.LOT_WIDTH} />
                                        <span>{userContext.selectedHome.lot_width}</span>
                                      </div>
                                    </div>
                                    <div className="dimensions">
                                      <div className="dimension">
                                        Area: {userContext.selectedHome.total_area_m}m&sup2;
                                      </div>
                                      <div className="spacer"></div>
                                      <div className="dimension">
                                        Width: {userContext.selectedHome.width}m
                                      </div>
                                      <div className="spacer"></div>
                                      <div className="dimension">
                                        Length: {userContext.selectedHome.length}m
                                      </div>
                                    </div>
                                  </div>
                                </>
                              )}
                              <div className="floorplan-tabs">
                                {apiContext.selectedSeries.homes && (
                                  <div className="tabs">
                                    {apiContext.selectedSeries.homes.map((home) => {
                                      return (
                                        <div key={home.id} 
                                          data-id={home.id} 
                                          className={`tab ${userContext.selectedHome && userContext.selectedHome.id == home.id ? "active" : "" }`}
                                          onClick={() => userContext.setSelectedHome(home)}
                                        >
                                          { home.title.replace((apiContext.selectedSeries ? apiContext.selectedSeries.title : "") + " ", "").replace(" Narrow", "N").replace(" Dual", "D") }
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                              {apiContext.selectedSeries.homes && apiContext.selectedSeries.homes.length > 1 && (
                                <div className="arrows">
                                  <button className="arrow prev" onClick={() => showPreviousHome()}><Icon name={IconName.ARROW_RED} /></button>
                                  <button className="arrow next" onClick={() => showNextHome()}><Icon name={IconName.ARROW_RED} /></button>
                                </div>
                              )}
                              <FloorplanCustomiser
                                home={userContext.selectedHome}
                                isActive={true}
                                isFloorplanFlipped={
                                  userContext.isFloorplanFlipped.get(userContext.selectedHome.id) ||
                                  false
                                }
                                onFlipFloorplan={(isFlipped) =>
                                  onFlipFloorplan(
                                    userContext.selectedHome ? userContext.selectedHome.id : 0, 
                                    isFlipped
                                  )
                                }
                                selectedOptions={
                                  userContext.selectedFloorplanOptions.get(
                                    userContext.selectedHome.id
                                  ) || []
                                }
                                onClickShowLightbox={onClickShowLightbox}
                                onClickOption={(floorplanOption) =>
                                  onClickFloorplanOption(
                                    userContext.selectedHome ? userContext.selectedHome.id : 0,
                                    floorplanOption as FloorplanOption
                                  )
                                }
                                altLayout={true}
                                onClickFloorplan={(id) => onSelectFloorplan(id)}
                              />
                            </div>
                          )
                        )}
                      </div>
                    </>
                  );
                }
              )}
              {apiContext.isLoadingNextPage ? (
                <div className="load-more-indicator">
                  <EllipsisLoader />
                </div>
              ) : apiContext.homeSearchCurrentPage < apiContext.homeSearchLastPage && (windowSize.width >= 992 || !hasChosenSeries) && (
                <div className="load-more-button">
                  <button className="btn" onClick={() => onLoadMore()}>Load More</button>
                </div>
              )}
            </div>
          ) : windowSize.width < 992 ? (
            <p className={"section-label no-results"}>
              No Homes Match Your Criteria
            </p>
          ) : null}
        </div>
      ): (
        <div className={"container section promo"}>
          {apiContext.chooseFloorplanSettings.promo_desktop_image && (
            <img
              className={"desktop"}
              src={apiContext.chooseFloorplanSettings.promo_desktop_image}
              alt={""}
            />
          )}
          {apiContext.chooseFloorplanSettings.promo_mobile_image && (
            <img
              className={"mobile"}
              src={apiContext.chooseFloorplanSettings.promo_mobile_image}
              alt={""}
            />
          )}
        </div>
      )}
      {windowSize.width <= 991 && isModalOpen && (
        <FullscreenModal
          title={"Refine Your Search"}
          onClose={onCloseSearchModal}
          closing={isModalClosing}
        >
          <div className={"container"}>
            <HouseFilters
              beds={apiContext.numBeds}
              living={apiContext.numLiving}
              baths={apiContext.numBaths}
              cars={apiContext.numCars}
              searchText={apiContext.searchText}
              onClickBeds={(val) => apiContext.toggleNumBeds(val)}
              onClickLiving={(val) => apiContext.toggleNumLiving(val)}
              onClickBaths={(val) => apiContext.toggleNumBaths(val)}
              onClickCars={(val) => apiContext.toggleNumCars(val)}
              onSearchChange={(val) => apiContext.setSearchText(val)}
              onScreenKeyboard={userContext.isKioskMode}
              inlineLabels
            />
            <button type="button" className="btn" onClick={() => onCloseSearchModal()}>Search</button>
          </div>
        </FullscreenModal>
      )}
      {isLightboxShown && userContext.selectedHome && (
        <FloorplanLightbox
          home={userContext.selectedHome}
          svg={lightboxSvg}
          selectedOptions={
            userContext.selectedFloorplanOptions.get(
              userContext.selectedHome.id
            ) || []
          }
          onClose={() => setIsLightboxShown(false)}
          isFloorplanFlipped={userContext.isFloorplanFlipped.get(userContext.selectedHome.id) ||false}
        />
      )}
      {userContext.isKioskMode && userContext.showKioskSplash && !userContext.hasKioskMenu && (
        <div className="splash" onClick={() => userContext.setShowKioskSplash(false)}>
          <img src={apiContext.kioskSplash} alt="Splash" />
        </div>
      )}
    </div>
  );
});

export default ChooseFloorplanScreen;
